import React from 'react';
import { useLocation, useParams, useMatch, Location, useNavigate } from 'react-router-dom';
import { useHistory } from './useHistory';

type Params<Key extends string = string> = {
  readonly [key in Key]: string;
};
// Define the match object structure to mimic React Router v5
interface Match<ParamKey extends string = string> {
  params: Params<ParamKey>;
  isExact: boolean;
  path: string;
  url: string;
}

// Define what props will be injected
export interface WithRouterProps {
  location: Location;
  history: ReturnType<typeof useHistory>;
  match: Match;
}

export function withRouter<P extends WithRouterProps>(
  Component: React.ComponentType<P>,
  // Allow an optional path to be passed for match
  matchPath?: string,
) {
  return function ComponentWithRouterProp(props: Omit<P, keyof WithRouterProps>) {
    const location = useLocation();
    const params = useParams() as Params;
    const history = useHistory();
    const navigate = useNavigate();

    // Create a match object similar to v5
    // If matchPath is provided, use it, otherwise use the current URL path
    const path = matchPath || location.pathname;
    const match = useMatch(path);

    // Create a match object that mimics the structure from React Router v5
    const v5Match: Match = {
      params,
      isExact: match !== null, // In v6, if the match is exact, useMatch returns non-null
      path,
      url: location.pathname,
    };

    return <Component {...(props as P)} location={location} navigate={navigate} history={history} match={v5Match} />;
  };
}
